import { ref } from "vue";

interface SearchOptions {
    minLength?: number;
}

export function useDebouncedSearch<T = any>(
    searchFunction: (query: string, signal?: AbortSignal) => Promise<T[]>,
    options: SearchOptions = {},
) {
    const { minLength = 0 } = options;

    const isLoading = ref(false);
    const results = ref<T[]>([]);
    const query = ref("");
    const error = ref<string | null>(null);

    let abortController: AbortController | null = null;

    const executeSearch = async (searchQuery: string) => {
        // Cancel previous request if it exists
        if (abortController) {
            abortController.abort();
        }

        // Create new AbortController for this request
        abortController = new AbortController();

        try {
            isLoading.value = true;
            error.value = null;

            const searchResults = await searchFunction(
                searchQuery,
                abortController.signal,
            );
            results.value = searchResults;
        } catch (err: any) {
            // Ignore aborted requests
            if (err.name !== "AbortError") {
                error.value = err.message || "Search failed";
                console.error("Search error:", err);
            }
        } finally {
            isLoading.value = false;
        }
    };

    const search = async (searchQuery: string) => {
        query.value = searchQuery;

        // Check minimum length
        if (searchQuery.length < minLength) {
            results.value = [];
            isLoading.value = false;
            return;
        }

        // Execute search immediately
        await executeSearch(searchQuery);
    };

    const clearResults = () => {
        results.value = [];
        query.value = "";
        error.value = null;
        isLoading.value = false;

        // Abort any pending request
        if (abortController) {
            abortController.abort();
            abortController = null;
        }
    };

    const cancelSearch = () => {
        if (abortController) {
            abortController.abort();
            abortController = null;
        }
        isLoading.value = false;
    };

    return {
        // State
        isLoading,
        results,
        query,
        error,
        // Methods
        search,
        clearResults,
        cancelSearch,
    };
}
